import React, { useState, useEffect } from 'react';
import {
  fetchMovies, fetchMovieOptions, updateMovieDetails,
  addAllowedMovie, updateAllowedMovie,
  setMovieCanonical, clearMovieCanonical,
  selectTmdbMatch, resetMovieTmdb,
  searchTmdbByText,
  setMovieFiltered,
} from '../api/client';
import { ageText } from './ageLabel';
import { NEU_BG, TEXT_PRIMARY, TEXT_SECONDARY, TEXT_TERTIARY, BORDER_SUBTLE } from '../theme';
import { useAuth } from '../context/AuthContext';

/**
 * A film's actions — approve, filter from statistics, link to another film,
 * match on TMDB, and (admins) its age rating, distributor and "Israeli".
 * The movies list opens it from a row's ⋮; the movie page from its "✎ עריכה"
 * button, so both offer exactly the same actions. Render it inside
 * AnchoredMenu: it is portalled, and its root stops clicks reaching the row.
 */
function MovieActionPanel({ movie, allMovies, onClose, onRefresh }) {
  const { hasPermission, user } = useAuth();
  const [subPanel, setSubPanel] = useState(null); // null | 'canonical' | 'tmdb' | 'details'
  // The movie page has no catalogue loaded; fetch it the first time a link is searched for.
  const [loadedMovies, setLoadedMovies] = useState(null);
  const catalogue = allMovies || loadedMovies || [];
  useEffect(() => {
    if (subPanel === 'canonical' && !allMovies && !loadedMovies) {
      fetchMovies().then(setLoadedMovies).catch(() => setLoadedMovies([]));
    }
  }, [subPanel, allMovies, loadedMovies]);
  const [canonicalSearch, setCanonicalSearch] = useState('');
  const [tmdbQuery, setTmdbQuery] = useState(movie.title_he || movie.title || '');
  const [tmdbResults, setTmdbResults] = useState(null);
  const [tmdbLoading, setTmdbLoading] = useState(false);
  const [tmdbError, setTmdbError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const toggleSub = (panel) => setSubPanel(p => p === panel ? null : panel);

  const doAllow = async () => {
    setActionLoading(true);
    try {
      if (movie.is_allowed) {
        await updateAllowedMovie(movie.allowed_id, false);
      } else if (movie.allowed_id) {
        // Approved once and later un-approved: the entry exists, so re-activate
        // it — adding it again is refused as a duplicate.
        await updateAllowedMovie(movie.allowed_id, true);
      } else {
        await addAllowedMovie(movie.title);
      }
      await onRefresh();
      onClose();
    } finally {
      setActionLoading(false);
    }
  };

  const doToggleFilter = async () => {
    setActionLoading(true);
    try {
      await setMovieFiltered(movie.id, !movie.is_filtered);
      await onRefresh();
      onClose();
    } finally {
      setActionLoading(false);
    }
  };

  const doSetCanonical = async (targetId) => {
    setActionLoading(true);
    try {
      await setMovieCanonical(movie.id, targetId);
      await onRefresh();
      onClose();
    } finally {
      setActionLoading(false);
    }
  };

  const doClearCanonical = async () => {
    setActionLoading(true);
    try {
      await clearMovieCanonical(movie.id);
      await onRefresh();
      onClose();
    } finally {
      setActionLoading(false);
    }
  };

  const doTmdbSearch = async () => {
    if (!tmdbQuery.trim()) return;
    setTmdbLoading(true);
    setTmdbError('');
    setTmdbResults(null);
    try {
      const data = await searchTmdbByText(tmdbQuery.trim());
      setTmdbResults(data.candidates || []);
    } catch (e) {
      setTmdbError('שגיאה בחיפוש TMDB');
    } finally {
      setTmdbLoading(false);
    }
  };

  const doSelectTmdb = async (tmdbId) => {
    setActionLoading(true);
    try {
      await selectTmdbMatch(movie.id, tmdbId);
      await onRefresh();
      onClose();
    } finally {
      setActionLoading(false);
    }
  };

  const doResetTmdb = async () => {
    setActionLoading(true);
    try {
      await resetMovieTmdb(movie.id);
      await onRefresh();
      onClose();
    } finally {
      setActionLoading(false);
    }
  };

  const canonicalCandidates = catalogue.filter(m =>
    m.id !== movie.id &&
    (
      m.title.toLowerCase().includes(canonicalSearch.toLowerCase()) ||
      (m.title_he && m.title_he.includes(canonicalSearch)) ||
      (m.title_en && m.title_en.toLowerCase().includes(canonicalSearch.toLowerCase()))
    )
  ).slice(0, 8);

  const panelStyle = {
    background: NEU_BG,
    border: `1px solid ${BORDER_SUBTLE}`,
    borderRadius: 10,
    padding: '12px 0',
    minWidth: 240,
    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
    zIndex: 9999,
  };

  const itemStyle = {
    padding: '10px 16px',
    cursor: 'pointer',
    fontSize: 13,
    color: TEXT_PRIMARY,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    borderBottom: `1px solid ${BORDER_SUBTLE}`,
    userSelect: 'none',
  };

  const subStyle = {
    padding: '10px 16px',
    background: 'var(--surf-alt)',
    borderBottom: `1px solid ${BORDER_SUBTLE}`,
  };

  return (
    <div style={panelStyle} onClick={e => e.stopPropagation()}>
      {/* Allow / Block */}
      {hasPermission('movies.approve_blocked') && (
        <div
          style={itemStyle}
          onClick={actionLoading ? undefined : doAllow}
        >
          {movie.is_allowed
            ? <><span>🚫</span> ביטול אישור הסרט</>
            : <><span>✅</span> אישור הסרט</>
          }
        </div>
      )}

      {/* Filter / Unfilter */}
      {hasPermission('movies.filter_movie') && (
        <div
          style={itemStyle}
          onClick={actionLoading ? undefined : doToggleFilter}
        >
          {movie.is_filtered
            ? <><span>🔓</span> ביטול סינון הסרט</>
            : <><span>🚫</span> סינון הסרט מסטטיסטיקה</>
          }
        </div>
      )}

      {/* Canonical link */}
      {hasPermission('movies.canonical_link') && <div style={{ ...itemStyle, flexDirection: 'column', alignItems: 'flex-start' }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', cursor: 'pointer' }}
          onClick={() => toggleSub('canonical')}
        >
          <span>🔗</span>
          <span>קישור לסרט אחר</span>
          {movie.canonical_movie_id && <span style={{ fontSize: 11, color: '#60a5fa', marginInlineStart: 'auto' }}>מקושר</span>}
          <span style={{ color: TEXT_TERTIARY, marginInlineStart: movie.canonical_movie_id ? 0 : 'auto' }}>
            {subPanel === 'canonical' ? '▲' : '▼'}
          </span>
        </div>
        {subPanel === 'canonical' && (
          <div style={{ width: '100%', marginTop: 8 }}>
            {movie.canonical_movie_id && (
              <button
                onClick={doClearCanonical}
                disabled={actionLoading}
                style={{
                  background: 'var(--danger-bg)', border: '1px solid rgba(239,68,68,0.3)', color: 'var(--danger)',
                  borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer',
                  fontFamily: 'Heebo, sans-serif', marginBottom: 8,
                }}
              >מחיקת קישור</button>
            )}
            <input
              type="text"
              placeholder="חפש סרט לקישור..."
              value={canonicalSearch}
              onChange={e => setCanonicalSearch(e.target.value)}
              style={{
                background: 'var(--surf-alt)', border: `1px solid ${BORDER_SUBTLE}`,
                borderRadius: 6, padding: '6px 10px', color: TEXT_PRIMARY,
                fontSize: 12, fontFamily: 'Heebo, sans-serif', width: '100%',
                outline: 'none', boxSizing: 'border-box',
              }}
            />
            <div style={{ marginTop: 4, maxHeight: 160, overflowY: 'auto' }}>
              {canonicalCandidates.map(m => (
                <div
                  key={m.id}
                  onClick={() => doSetCanonical(m.id)}
                  style={{
                    padding: '6px 8px', cursor: 'pointer', borderRadius: 4, fontSize: 12,
                    color: TEXT_PRIMARY,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surf-hov)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {m.title_he || m.title}
                  {m.title_en && <span style={{ color: TEXT_TERTIARY, marginInlineStart: 6 }}>{m.title_en}</span>}
                </div>
              ))}
              {canonicalCandidates.length === 0 && canonicalSearch && (
                <div style={{ color: TEXT_TERTIARY, fontSize: 12, padding: '6px 8px' }}>לא נמצאו תוצאות</div>
              )}
            </div>
          </div>
        )}
      </div>}

      {/* TMDB search */}
      {hasPermission('movies.tmdb_match') && <div style={{ ...itemStyle, flexDirection: 'column', alignItems: 'flex-start', borderBottom: user?.is_admin ? itemStyle.borderBottom : 'none' }}>
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', cursor: 'pointer' }}
          onClick={() => toggleSub('tmdb')}
        >
          <span>🎬</span>
          <span>חיפוש ב-TMDB</span>
          {movie.tmdb_id && <span style={{ fontSize: 11, color: '#60a5fa', marginInlineStart: 'auto' }}>מקושר</span>}
          <span style={{ color: TEXT_TERTIARY, marginInlineStart: movie.tmdb_id ? 0 : 'auto' }}>
            {subPanel === 'tmdb' ? '▲' : '▼'}
          </span>
        </div>
        {subPanel === 'tmdb' && (
          <div style={{ width: '100%', marginTop: 8 }}>
            {movie.tmdb_id && (
              <button
                onClick={doResetTmdb}
                disabled={actionLoading}
                style={{
                  background: 'var(--danger-bg)', border: '1px solid rgba(239,68,68,0.3)', color: 'var(--danger)',
                  borderRadius: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer',
                  fontFamily: 'Heebo, sans-serif', marginBottom: 8,
                }}
              >אפס TMDB</button>
            )}
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                type="text"
                value={tmdbQuery}
                onChange={e => setTmdbQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && doTmdbSearch()}
                style={{
                  flex: 1, background: 'var(--surf-alt)', border: `1px solid ${BORDER_SUBTLE}`,
                  borderRadius: 6, padding: '6px 10px', color: TEXT_PRIMARY,
                  fontSize: 12, fontFamily: 'Heebo, sans-serif', outline: 'none',
                }}
              />
              <button
                onClick={doTmdbSearch}
                disabled={tmdbLoading}
                style={{
                  background: 'var(--accent)', border: 'none', color: '#fff', borderRadius: 6,
                  padding: '6px 12px', fontSize: 12, cursor: 'pointer',
                  fontFamily: 'Heebo, sans-serif',
                }}
              >{tmdbLoading ? '...' : 'חפש'}</button>
            </div>
            {tmdbError && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 6 }}>{tmdbError}</div>}
            {tmdbResults && (
              <div style={{ marginTop: 6, maxHeight: 280, overflowY: 'auto' }}>
                {tmdbResults.length === 0 && (
                  <div style={{ color: TEXT_TERTIARY, fontSize: 12 }}>לא נמצאו תוצאות</div>
                )}
                {tmdbResults.map(r => (
                  <div
                    key={r.tmdb_id}
                    style={{
                      display: 'flex', gap: 8, padding: '8px 0',
                      borderBottom: `1px solid ${BORDER_SUBTLE}`, alignItems: 'flex-start',
                    }}
                  >
                    {r.poster_url && (
                      <img
                        src={r.poster_url}
                        alt=""
                        style={{ width: 36, height: 54, borderRadius: 4, objectFit: 'cover', flexShrink: 0 }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: TEXT_PRIMARY }}>
                        {r.title_he || r.title}
                      </div>
                      {r.title_en && <div style={{ fontSize: 11, color: TEXT_TERTIARY }}>{r.title_en}</div>}
                      {r.year && <div style={{ fontSize: 11, color: TEXT_SECONDARY }}>{r.year}</div>}
                      {r.overview_he && (
                        <div style={{
                          fontSize: 11, color: TEXT_TERTIARY, marginTop: 2,
                          overflow: 'hidden', display: '-webkit-box',
                          WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                        }}>
                          {r.overview_he}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => doSelectTmdb(r.tmdb_id)}
                      disabled={actionLoading}
                      style={{
                        background: 'var(--ok)', border: 'none', color: '#fff', borderRadius: 6,
                        padding: '4px 10px', fontSize: 11, cursor: 'pointer',
                        fontFamily: 'Heebo, sans-serif', flexShrink: 0,
                      }}
                    >בחר</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>}

      {/* Age rating, distributor, Israeli — admins only (§12.11) */}
      {user?.is_admin && <div style={{ ...itemStyle, flexDirection: 'column', alignItems: 'flex-start', borderBottom: 'none' }}>
        <div
          data-menu-details
          style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', cursor: 'pointer' }}
          onClick={() => toggleSub('details')}
        >
          <span>✏️</span>
          <span>עריכת פרטים</span>
          <span style={{ color: TEXT_TERTIARY, marginInlineStart: 'auto' }}>{subPanel === 'details' ? '▲' : '▼'}</span>
        </div>
        {subPanel === 'details' && (
          <DetailsForm movie={movie} onSaved={async () => { await onRefresh(); onClose(); }} />
        )}
      </div>}
    </div>
  );
}

// Choices only, from the admin's lists — nothing here is typed (§12.11).
function DetailsForm({ movie, onSaved }) {
  const [lists, setLists] = useState(null);
  const [age, setAge] = useState(movie.age_rating_id ? String(movie.age_rating_id) : '');
  const [dist, setDist] = useState(movie.distributor_id ? String(movie.distributor_id) : '');
  const [isr, setIsr] = useState(movie.is_israeli_override == null ? '' : movie.is_israeli_override ? 'yes' : 'no');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { fetchMovieOptions().then(setLists).catch(() => setError('שגיאה בטעינת הרשימות')); }, []);

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      await updateMovieDetails(movie.id, {
        age_rating_id: age ? Number(age) : null,
        distributor_id: dist ? Number(dist) : null,
        is_israeli: isr === '' ? null : isr === 'yes',
      });
      await onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || 'השמירה נכשלה');
      setSaving(false);
    }
  };

  const lbl = { fontSize: 11, fontWeight: 700, color: TEXT_TERTIARY, margin: '8px 0 4px', display: 'flex', gap: 6, alignItems: 'center' };
  const sel = {
    width: '100%', background: 'var(--surf-alt)', border: `1px solid ${BORDER_SUBTLE}`, borderRadius: 6,
    padding: '6px 8px', color: TEXT_PRIMARY, fontSize: 12, fontFamily: 'Heebo, sans-serif', outline: 'none',
  };
  if (!lists) return <div style={{ fontSize: 12, color: TEXT_TERTIARY, marginTop: 8 }}>{error || 'טוען...'}</div>;
  return (
    <div style={{ width: '100%', marginTop: 4 }} data-details-form>
      <div style={lbl}>הגבלת גיל {movie.age_tmdb && <span dir="ltr" style={{ unicodeBidi: 'isolate', fontWeight: 400 }}>TMDB: {movie.age_tmdb}</span>}</div>
      <select data-field="age" style={sel} value={age} onChange={e => setAge(e.target.value)}>
        <option value="">אוטומטי ({movie.age_tmdb_label ? ageText(movie.age_tmdb_label) : 'אין'})</option>
        {lists.ages.map(a => <option key={a.id} value={a.id}>{ageText(a.name)}</option>)}
      </select>
      <div style={lbl}>מפיץ</div>
      <select data-field="distributor" style={sel} value={dist} onChange={e => setDist(e.target.value)}>
        <option value="">ללא</option>
        {lists.distributors.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
      </select>
      {lists.distributors.length === 0 && <div style={{ fontSize: 11, color: TEXT_TERTIARY, marginTop: 3 }}>הוספה בעמוד ניהול</div>}
      <div style={lbl}>ישראלי</div>
      <select data-field="israeli" style={sel} value={isr} onChange={e => setIsr(e.target.value)}>
        <option value="">אוטומטי ({movie.is_israeli_auto ? 'כן' : 'לא'})</option>
        <option value="yes">כן</option>
        <option value="no">לא</option>
      </select>
      <button data-save onClick={save} disabled={saving} style={{
        marginTop: 10, width: '100%', background: 'var(--accent)', border: 'none', color: '#fff', borderRadius: 6,
        padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'Heebo, sans-serif',
        opacity: saving ? 0.6 : 1,
      }}>שמור</button>
      {error && <div style={{ color: 'var(--danger)', fontSize: 12, marginTop: 6 }}>{error}</div>}
    </div>
  );
}

export default MovieActionPanel;
